import React, { FormEvent, memo, useCallback, useMemo, useState } from 'react';
import { View, TextInput, TouchableOpacity } from 'react-native';
import { Button, Text, YStack } from 'tamagui';
import { FontAwesome, FontAwesome6 } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import styles from '@stylesheet';
import { PasskeyLoginButton } from '@components/passkey/PasskeyLoginButton';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import {
  getRememberMePreference,
  saveRememberMePreference,
} from '@utils/persistentSessionStorage';
import { useConditionalPasskey } from '@hooks/useConditionalPasskey';

type LoginSignInFormProps = {
  theme: boolean;
  isConnected: boolean;
  onSubmit: (values: { email: string; password: string; rememberMe?: boolean }) => void;
  onPasskeyLoginSuccess?: (result: { success?: boolean; data?: { id?: string } }) => void;
  onError?: (message: string) => void;
  onOffline?: () => void;
};

const FORM_MAX_WIDTH = 400;

const LoginSignInForm = ({
  theme,
  isConnected,
  onSubmit,
  onPasskeyLoginSuccess,
  onError,
  onOffline,
}: LoginSignInFormProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [rememberMe, setRememberMe] = useState(() => getRememberMePreference());

  useConditionalPasskey({
    enabled: Boolean(onPasskeyLoginSuccess),
    onSuccess: onPasskeyLoginSuccess,
    onError: onError,
  });

  const colors = useMemo(
    () => ({
      inputPlaceholder: theme ? 'rgba(100, 116, 139, 0.7)' : 'rgba(148, 163, 184, 0.75)',
      inputBackground: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
      inputBorder: theme ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.35)',
      inputText: theme ? '#0f172a' : '#e2e8f0',
      iconMuted: theme ? '#64748b' : '#94a3b8',
      primaryButton: '#3B82F6',
    }),
    [theme],
  );

  const inputContainerStyle = useMemo(
    () => [
      styles.loginInputContainer,
      styles.loginInputContainerWebStable,
      {
        backgroundColor: colors.inputBackground,
        borderColor: colors.inputBorder,
        width: '100%',
        maxWidth: FORM_MAX_WIDTH,
      },
    ],
    [colors.inputBackground, colors.inputBorder],
  );

  const inputStyle = useMemo(
    () => [styles.loginInput, { color: colors.inputText, width: '100%' }],
    [colors.inputText],
  );

  const handleSignIn = useCallback(() => {
    if (!isConnected) {
      onOffline?.();
      return;
    }
    onSubmit({ email: email.trim(), password, rememberMe });
  }, [email, password, rememberMe, isConnected, onOffline, onSubmit]);

  const handleFormSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault?.();
      handleSignIn();
    },
    [handleSignIn],
  );

  return (
    <YStack
      tag="form"
      className="login-signInForm"
      gap={20}
      paddingBottom={15}
      width="100%"
      maxWidth={FORM_MAX_WIDTH}
      alignSelf="center"
      {...({
        onSubmit: handleFormSubmit,
        noValidate: true,
      } as any)}
    >
      <View style={inputContainerStyle}>
        <TextInput
          style={inputStyle}
          placeholder={t('Email or WhatsApp number')}
          placeholderTextColor={colors.inputPlaceholder}
          value={email ?? ''}
          onChangeText={setEmail}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="username webauthn"
          textContentType="username"
          inputMode="text"
          importantForAutofill="yes"
          testID="emailInput"
          accessibilityLabel="emailInput"
          {...({
            name: 'email',
            type: 'email',
            id: 'login-email',
            spellCheck: false,
          } as any)}
        />
      </View>

      <View style={inputContainerStyle}>
        <TextInput
          style={inputStyle}
          placeholder={t('password')}
          placeholderTextColor={colors.inputPlaceholder}
          value={password ?? ''}
          onChangeText={setPassword}
          secureTextEntry={passwordHidden}
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="current-password"
          textContentType="password"
          importantForAutofill="yes"
          testID="passwordInput"
          accessibilityLabel="passwordInput"
          onSubmitEditing={handleSignIn}
          {...({
            name: 'password',
            type: passwordHidden ? 'password' : 'text',
            id: 'login-password',
            spellCheck: false,
          } as any)}
        />
        <TouchableOpacity
          onPress={() => setPasswordHidden((hidden) => !hidden)}
          style={[styles.loginPasswordToggle, styles.loginPasswordToggleWebStable]}
          accessibilityRole="button"
          accessibilityLabel={passwordHidden ? t('showPassword') : t('hidePassword')}
        >
          <FontAwesome6
            style={{ color: passwordHidden ? colors.iconMuted : colors.primaryButton }}
            name={passwordHidden ? 'eye' : 'eye-slash'}
            size={20}
          />
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        onPress={() => {
          const next = !rememberMe;
          setRememberMe(next);
          saveRememberMePreference(next);
        }}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          gap: 10,
          maxWidth: FORM_MAX_WIDTH,
        }}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: rememberMe }}
        accessibilityLabel={t('staySignedIn')}
      >
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            borderWidth: 1.5,
            borderColor: rememberMe ? colors.primaryButton : colors.inputBorder,
            backgroundColor: rememberMe ? colors.primaryButton : 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {rememberMe ? (
            <FontAwesome name="check" size={12} color="#fff" />
          ) : null}
        </View>
        <Text style={{ color: colors.inputText, fontSize: responsiveFontSize(14) }}>
          {t('staySignedIn')}
        </Text>
      </TouchableOpacity>

      <Button
        backgroundColor={colors.primaryButton}
        size="$4"
        width="100%"
        maxWidth={FORM_MAX_WIDTH}
        testID="signInButton"
        accessible
        accessibilityLabel="signInButton"
        onPress={handleSignIn}
        {...({ type: 'submit' } as any)}
        icon={<FontAwesome name="user" color="white" size={24} />}
      >
        <Text style={styles.loginSubmitButtonText}>{t('Sign In')}</Text>
      </Button>

      {onPasskeyLoginSuccess ? (
        <PasskeyLoginButton
          email={email.trim()}
          onSuccess={onPasskeyLoginSuccess}
          onError={(message) => onError?.(message)}
        />
      ) : null}
    </YStack>
  );
};

export default memo(LoginSignInForm);
